<template>
  <div class="ln-page">
    <div class="ln-wrap">
      <header class="ln-head" data-reveal>
        <div class="ln-head__text">
          <p class="ln-eyebrow">Dashboard</p>
          <h1 class="ln-display">Links</h1>
          <p class="ln-lede">
            Every short link on this account, ready to search, edit or share.
          </p>
        </div>

        <div class="ln-head__actions">
          <button
            v-if="canWrite"
            type="button"
            class="ln-btn ln-btn--solid"
            @click="openDialogForNewLink()"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
            New Link
          </button>
          <a
            class="ln-btn ln-btn--quiet"
            :href="userPlan.user.metabase_analytics_url"
            target="_blank"
            rel="noopener"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-chart-bar</v-icon>
            Analytics overview
          </a>
        </div>
      </header>

      <section class="ln-panel" data-reveal>
        <div class="ln-panel__head">
          <div class="ln-search">
            <v-icon size="small" class="ln-search__icon">mdi-magnify</v-icon>
            <input
              v-model="search"
              type="search"
              class="ln-search__input"
              placeholder="Search links by short handle, note"
              aria-label="Search links by short handle, note"
              @input="fetchEntriesDebounced"
            />
            <button
              v-if="search"
              type="button"
              class="ln-search__clear"
              aria-label="Clear search"
              @click="clearSearch"
            >
              <v-icon size="small">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- mobile-breakpoint is pinned to the 600px Vuetify 2 used: the v4
             default ("lg", 1145px) would stack the rows on every laptop, and
             the column width hints below are written for the wide table -->
        <v-data-table-server
          :headers="headers"
          :items="linkList"
          :loading="tableLoading"
          :items-length="totalLinksCount"
          :page="tableOptions.page"
          :items-per-page="tableOptions.itemsPerPage"
          :mobile-breakpoint="600"
          :items-per-page-options="[10, 25, 50, 100]"
          @update:options="onTableOptions"
        >
          <!-- add chart https://dmtrbrl.github.io/vue-trend-chart/ -->
          <template v-slot:item.appInfo="{ item }">
            <div class="ln-app">
              <span v-if="item.landing_page" class="ln-cell-strong">{{
                item.landing_page.title
              }}</span>
              <span
                v-else-if="item.deeplinking_domain"
                class="ln-cell-strong"
                >{{ item.deeplinking_domain.appName }}</span
              >
              <span v-else class="ln-cell-empty">Not available</span>
              <v-tooltip location="bottom" v-if="item.type === 'deeplinking'">
                <template v-slot:activator="{ props }">
                  <span class="ln-tag ln-tag--blue" v-bind="props"
                    >Deep link</span
                  >
                </template>
                <span
                  >Deep Linking support with
                  {{ item.deeplinking_domain.appName }}</span
                >
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.created_at="{ item }">
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <span class="ln-cell-meta" v-bind="props">
                  {{ formatTime(item.created_at) }}
                </span>
              </template>
              <span>{{ new Date(item.created_at).toLocaleString() }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.fullUrl="{ item }">
            <div class="ln-url">
              <span class="ln-url__text">{{ item.full_url }}</span>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn ln-url__copy"
                    aria-label="Copy to clipboard"
                    v-bind="props"
                    @click="copyToClipboard(item.full_url, item.id)"
                  >
                    <v-icon size="small">{{
                      copiedLinkId === item.id
                        ? "mdi-check"
                        : "mdi-content-copy"
                    }}</v-icon>
                  </button>
                </template>
                <span>Copy to clipboard</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.note="{ item }">
            <div class="ln-note">{{ item.note }}</div>
          </template>
          <template v-slot:item.clicks_count="{ item }">
            <span class="ln-cell-num">{{
              formatCount(item.clicks_count)
            }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="ln-actions">
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn"
                    aria-label="Share"
                    @click="openLinkDialogForShare(item.full_url, item.id)"
                    v-bind="props"
                  >
                    <v-icon size="small">mdi-share</v-icon>
                  </button>
                </template>
                <span>Share</span>
              </v-tooltip>
              <v-tooltip v-if="canWrite" location="bottom">
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn"
                    aria-label="Edit"
                    @click="editLink(item.id)"
                    v-bind="props"
                  >
                    <v-icon size="small">mdi-pencil</v-icon>
                  </button>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <a
                    class="ln-iconbtn"
                    aria-label="See detailed analytics for this link"
                    :href="linkAnalyticsUrl(item.id)"
                    target="_blank"
                    rel="noopener"
                    v-bind="props"
                  >
                    <v-icon size="small">mdi-chart-areaspline-variant</v-icon>
                  </a>
                </template>
                <span>See detailed analytics for this link</span>
              </v-tooltip>
              <v-tooltip v-if="canWrite" location="bottom">
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn ln-iconbtn--danger"
                    aria-label="Delete"
                    @click="openDeleteDialog(item)"
                    v-bind="props"
                  >
                    <v-icon size="small">mdi-delete</v-icon>
                  </button>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:no-data>
            <!-- a search with no match used to show "No links yet" below,
                 which looked like every link on the account had gone -->
            <div v-if="listKeyword" class="ln-empty">
              <p class="ln-empty__title">No links match “{{ listKeyword }}”</p>
              <p class="ln-empty__body">
                The search looks at the short handle, the short link and the
                note.
              </p>
              <button
                type="button"
                class="ln-btn ln-btn--quiet"
                @click="clearSearch"
              >
                Clear search
              </button>
            </div>
            <div v-else class="ln-empty">
              <svg
                class="ln-empty__art"
                width="96"
                height="96"
                viewBox="0 0 96 96"
                aria-hidden="true"
                focusable="false"
              >
                <rect
                  x="56"
                  y="10"
                  width="28"
                  height="28"
                  fill="var(--ln-yellow-bg)"
                />
                <g
                  transform="rotate(-45 48 48)"
                  fill="none"
                  stroke="var(--ln-ink)"
                  stroke-width="2.5"
                  stroke-linecap="round"
                >
                  <path d="M42 34H31a14 14 0 0 0 0 28h11" />
                  <path d="M54 34h11a14 14 0 0 1 0 28H54" />
                  <path d="M34 48h28" />
                </g>
              </svg>
              <p class="ln-empty__title">No links yet</p>
              <p class="ln-empty__body">
                Short links you create will be listed here, with their clicks
                for the current month.
              </p>
              <button
                v-if="canWrite"
                type="button"
                class="ln-btn ln-btn--solid"
                @click="openDialogForNewLink()"
              >
                <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
                New Link
              </button>
            </div>
          </template>
        </v-data-table-server>
      </section>
    </div>

    <link-dialog ref="linkDialog" />

    <v-dialog v-model="deleteDialog" max-width="480">
      <div class="ln-dialog">
        <h2 class="ln-dialog__title">Delete link</h2>
        <div class="ln-dialog__body">
          <p>
            This action will permanently delete the selected link. This cannot
            be undone. Ensure the link is not actively used by any users.
          </p>
          <p class="ln-dialog__note">
            This won't delete any analytics data associated with the link.
          </p>
          <p class="ln-dialog__label">Link</p>
          <p class="ln-dialog__target">{{ linkToDeleteUrl }}</p>
        </div>
        <div class="ln-dialog__actions">
          <button
            type="button"
            class="ln-btn ln-btn--quiet"
            @click="onDeleteDialogCancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="ln-btn ln-btn--danger"
            :disabled="confirmDisabled || deleting"
            @click="confirmDeleteLink"
          >
            Yes, delete link
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import {
  newLink as newLinkRequest,
  listLinks,
  getLinkByLinkId,
  deleteLink,
  handleApiError,
} from "../api";
import { mapGetters, mapState } from "vuex";
import LinkDialog from "../components/LinkDialog.vue";
import eventBus from "../eventBus";
import { format as formatTime } from "timeago.js";

export default {
  components: {
    LinkDialog,
  },
  data: () => ({
    linkIdToLoad: null,
    deleteDialog: false,
    linkToDeleteId: null,
    linkToDeleteUrl: "",
    confirmDisabled: false,
    // a delete request is in flight (a double click must not send two)
    deleting: false,
    // not proxied onto the instance by Vue (leading "_"); assigned as a plain property
    // eslint-disable-next-line vue/no-reserved-keys
    _confirmTimerId: null,
    copiedLinkId: null,
    tableOptions: {
      itemsPerPage: 10,
      page: 1,
    },
    tableLoading: true,
    linkListRes: {},
    linkList: [],
    totalLinksCount: 0,
    search: "",
    // the keyword linkList was fetched with, for the empty state: `search`
    // is the field as typed, ahead of the debounced request
    listKeyword: "",
    headers: [
      {
        title: "App",
        align: "start",
        sortable: false,
        key: "appInfo",
      },
      {
        title: "URL",
        key: "fullUrl",
        sortable: false,
        // without a width hint the auto table layout treats this as the one
        // "flexible" column once Note/Actions/Clicks claim their natural
        // space, and since overflow-wrap:anywhere lets it break mid-token,
        // it gets squeezed to a near-zero-width column of single wrapped
        // characters at viewports around 600-960px instead of triggering the
        // wrapper's horizontal scroll like every other column does
        width: "220px",
      },
      {
        title: "Note",
        key: "note",
        sortable: false,
        align: "start",
        width: "200px",
      },
      {
        title: "Clicks this month",
        key: "clicks_count",
        align: "end",
        sortable: false,
      },
      {
        title: "Created",
        key: "created_at",
        align: "start",
        sortable: false,
      },
      { title: "Actions", key: "actions", align: "end", sortable: false },
    ],
  }),
  async created() {
    // awaited first so initialize() reads the loaded links_count, but caught:
    // an unhandled rejection here abandoned the rest of this hook, and since
    // App.vue's overlay is bound to store.isLoading, one failing request left
    // the whole app behind the spinner for good
    try {
      await this.$store.dispatch("getUserPlan");
    } catch (err) {
      handleApiError(err);
    }
    // initialize() reports its own failures and never rejects
    await this.initialize();
    this.$store.commit("setIsLoading", false);

    this.$store.dispatch("getAvailableDomains");
    this._onLinkListFetch = async () => {
      // await first: initialize() reads userPlan.user.links_count
      // synchronously for the pagination total, so the store must already
      // hold the post-create/update count before initialize() runs, or that
      // total (unlike the "Links created" stat, which is a reactive getter)
      // stays stale until the next unrelated refresh
      try {
        await this.$store.dispatch("getUserPlan");
      } catch (err) {
        handleApiError(err);
      }
      this.initialize();
    };
    eventBus.on("linkListFetch", this._onLinkListFetch);
  },
  mounted() {
    this.setupReveal();
  },
  beforeUnmount() {
    if (this._onLinkListFetch) {
      eventBus.off("linkListFetch", this._onLinkListFetch);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  computed: {
    ...mapState(["userPlan"]),
    ...mapGetters(["canWrite"]),
  },
  methods: {
    formatCount(value) {
      return typeof value === "number" ? value.toLocaleString() : value;
    },
    linkAnalyticsUrl(linkId) {
      const url = this.userPlan.user.metabase_analytics_url;
      if (!url) return undefined;
      return url.replace("#", "?link_id=" + linkId + "#");
    },
    setupReveal() {
      const elements = this.$el.querySelectorAll("[data-reveal]");
      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // no observer (or the user asked for no motion): show everything at once
      // rather than leaving the page permanently at opacity 0
      if (!("IntersectionObserver" in window) || prefersReducedMotion) {
        elements.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.05 },
      );
      elements.forEach((el) => this._revealObserver.observe(el));
    },
    clearSearch() {
      this.search = "";
      this.fetchEntriesDebounced();
    },
    copyToClipboard(text, linkId) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.copiedLinkId = linkId;
          setTimeout(() => {
            if (this.copiedLinkId === linkId) {
              this.copiedLinkId = null;
            }
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy URL: ", err);
        });
    },
    formatTime(time) {
      return formatTime(time);
    },
    openDeleteDialog(item) {
      this.linkToDeleteId = item.id;
      this.linkToDeleteUrl = item.full_url;
      this.deleteDialog = true;
      if (this._confirmTimerId) {
        clearTimeout(this._confirmTimerId);
        this._confirmTimerId = null;
      }
      this.confirmDisabled = true;
      this._confirmTimerId = setTimeout(() => {
        this.confirmDisabled = false;
        this._confirmTimerId = null;
      }, 2000);
    },
    async confirmDeleteLink() {
      if (this.deleting) return;
      this.deleting = true;
      try {
        if (typeof this.linkToDeleteId !== "number") {
          window.alert("Invalid link selected");
          return;
        }
        if (this._confirmTimerId) {
          clearTimeout(this._confirmTimerId);
          this._confirmTimerId = null;
        }
        await deleteLink(this.linkToDeleteId);
        this.deleteDialog = false;
        this.linkToDeleteId = null;
        this.linkToDeleteUrl = "";
        this.confirmDisabled = false;
        await this.initialize();
        this.$store.dispatch("getUserPlan");
      } catch (err) {
        window.alert(handleApiError(err, "Failed to delete link"));
      } finally {
        this.deleting = false;
      }
    },
    onDeleteDialogCancel() {
      if (this._confirmTimerId) {
        clearTimeout(this._confirmTimerId);
        this._confirmTimerId = null;
      }
      this.deleteDialog = false;
      this.confirmDisabled = false;
      this.linkToDeleteId = null;
      this.linkToDeleteUrl = "";
    },
    async editLink(id) {
      // get link
      // set parameters
      if (typeof id !== "number") {
        window.alert("Failed to load link");
        return;
      }
      const linkDialog = this.$refs.linkDialog;
      linkDialog.init(id);
      this.openLinkDialog();
    },
    openLinkDialog() {
      const linkDialog = this.$refs.linkDialog;
      linkDialog.open();
    },
    openLinkDialogForShare(fullUrl, linkId) {
      const linkDialog = this.$refs.linkDialog;
      linkDialog.openShareDialog(fullUrl, linkId);
    },
    openDialogForNewLink() {
      const linkDialog = this.$refs.linkDialog;
      linkDialog.init();

      this.openLinkDialog();
    },
    fetchEntriesDebounced() {
      // prevent dialog open bug
      this.dialog = false;

      // cancel pending call
      clearTimeout(this._timerId);

      // delay new call 500ms
      this._timerId = setTimeout(() => {
        console.log("debounce done");
        this.onSearch();
      }, 500);
    },
    // a search result is always shown from its first page. Reset the bound
    // page before fetching: the smaller total would otherwise make Vuetify
    // clamp the page to 1 afterwards, and that update:options would differ
    // from tableOptions and trigger a second identical request
    onSearch() {
      this.tableOptions = { ...this.tableOptions, page: 1 };
      return this.initialize();
    },
    // the table's `update:options` payload is 1-based, the API is
    // offset-based
    listWindow({ page, itemsPerPage }) {
      return { take: itemsPerPage, skip: (page - 1) * itemsPerPage };
    },
    // Vuetify 4 emits update:options immediately on mount (the watcher in
    // VDataTable's useOptions is `immediate`), echoing back the page and
    // items-per-page it was handed. That mount echo would duplicate the
    // fetch created() already does - and created() has to keep it, since
    // initialize() reads userPlan.user.links_count for the row total and
    // must run after getUserPlan resolves. So fetch only when the window
    // actually moved, which also covers the page Vuetify resets by itself
    // when items-length drops below the current page.
    onTableOptions({ page, itemsPerPage }) {
      if (
        page === this.tableOptions.page &&
        itemsPerPage === this.tableOptions.itemsPerPage
      ) {
        return;
      }
      this.tableOptions = { page, itemsPerPage };
      this.initialize();
    },
    async initialize() {
      // a request made while one is in flight (e.g. paging twice quickly) is
      // remembered and served once, with the latest options, when it settles
      if (this._initializing) {
        this._refetch = true;
        return;
      }
      this._initializing = true;
      try {
        this.tableLoading = true;
        console.log("initialize called");
        const keyword = this.search;
        this.linkListRes = await listLinks({
          keyword,
          ...this.listWindow(this.tableOptions),
        });
        this.linkList = this.linkListRes.result;
        this.listKeyword = keyword;
        console.log("search:", this.search);
        console.log("userplan", this.userPlan.user);
        console.log("links list count", this.linkListRes.count);

        this.totalLinksCount =
          this.search && this.search.length > 0
            ? this.linkListRes.count
            : this.userPlan.user.links_count || 0;

        console.log("links found", this.totalLinksCount);
      } catch (err) {
        console.error(err);
        window.alert(handleApiError(err));
      } finally {
        // in the finally, not the try: a failed request used to leave the
        // table on its "Loading items..." row for the rest of the session
        this.tableLoading = false;
        this._initializing = false;
      }
      if (this._refetch) {
        this._refetch = false;
        await this.initialize();
      }
    },
  },
};
</script>

<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  padding: 20px 16px 40px;
}

/* ---------- scroll entry ---------------------------------------------- */

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 600ms var(--ln-ease),
    transform 600ms var(--ln-ease);
}

[data-reveal].is-revealed {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  [data-reveal],
  [data-reveal].is-revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ---------- page header ------------------------------------------------ */

.ln-head {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 0;
  max-width: 46ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

.ln-head__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* ---------- buttons ---------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-page a.ln-btn--quiet {
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn--danger {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-btn--danger:hover {
  background: #fbdfe1;
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

/* ---------- panel ------------------------------------------------------ */

.ln-panel {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  overflow: hidden;
}

.ln-panel__head {
  padding: 8px 12px;
  border-bottom: 1px solid var(--ln-line);
}

.ln-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
}

.ln-search__icon.v-icon {
  font-size: 17px;
  color: var(--ln-muted);
}

.ln-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-family: sans-serif;
  font-size: 14px;
  color: var(--ln-ink);
  -webkit-appearance: none;
  appearance: none;
}

.ln-search__input::placeholder {
  color: var(--ln-muted);
}

.ln-search__input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

.ln-search__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  color: var(--ln-muted);
  cursor: pointer;
}

.ln-search__clear:hover {
  background: var(--ln-surface-2);
  color: var(--ln-ink);
}

.ln-search__clear .v-icon {
  font-size: 15px;
  color: inherit;
}

/* ---------- table ------------------------------------------------------ */

/* every vuetify rule reached from here lives in an @layer, and unlayered
   styles beat any layer regardless of specificity - hence no !important
   below, unlike the Vuetify 2 version of this block */
.ln-panel :deep(.v-data-table) {
  --v-table-header-height: 42px;
  --v-table-row-height: 60px;
  background: transparent;
  color: var(--ln-ink-2);
}

.ln-panel :deep(.v-table__wrapper) {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.ln-panel :deep(th.v-data-table__th) {
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--ln-muted);
  background: var(--ln-surface-2);
  border-bottom: 1px solid var(--ln-line);
}

.ln-panel :deep(td.v-data-table__td) {
  font-size: 14px;
  color: var(--ln-ink-2);
  border-bottom: 1px solid var(--ln-line);
}

.ln-panel :deep(.v-data-table__tbody > tr:last-child > td.v-data-table__td) {
  border-bottom: 0;
}

.ln-panel :deep(.v-data-table__tbody > .v-data-table__tr:hover > td) {
  background: var(--ln-surface-2);
}

/* the placeholder rows are a single full-width <td>: no row chrome on them */
.ln-panel :deep(.v-data-table-rows-no-data > td),
.ln-panel :deep(.v-data-table-rows-loading > td) {
  height: auto;
  background: transparent;
  border-bottom: 0;
  color: inherit;
}

/* ---------- table, stacked below 600px --------------------------------- */

/* below mobile-breakpoint each record becomes one
   <tr class="v-data-table__tr--mobile"> whose <td>s are two-column grids of
   .v-data-table__td-title / .v-data-table__td-value */
.ln-panel :deep(.v-data-table__tr--mobile) {
  --v-table-row-height: 42px;
}

/* vuetify clears this itself, but from inside @layer vuetify-overrides,
   which the unlayered td rule above outranks - restate it, or the hairline
   lands once per field instead of once per record */
.ln-panel :deep(.v-data-table__tr--mobile > td:not(:last-child)) {
  border-bottom: 0;
}

.ln-panel :deep(.v-data-table__td-title) {
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-panel :deep(.v-data-table__td-value) {
  font-size: 14px;
  color: var(--ln-ink-2);
}

/* text-align:end doesn't reach the contents of a flex row */
.ln-panel :deep(.v-data-table__td-value) > .ln-app,
.ln-panel :deep(.v-data-table__td-value) > .ln-url {
  justify-content: flex-end;
}

.ln-panel :deep(.v-data-table-footer) {
  min-height: 52px;
  border-top: 1px solid var(--ln-line);
  font-family: var(--ln-mono);
  font-size: 11px;
  letter-spacing: 0.03em;
  color: var(--ln-muted);
}

.ln-panel :deep(.v-data-table-footer .v-field) {
  font-family: var(--ln-mono);
  font-size: 11px;
  color: var(--ln-muted);
}

/* .long/.short are the two animated bars inside the indeterminate wrapper;
   colouring the wrapper itself would paint one solid full-width bar */
.ln-panel :deep(.v-data-table-progress .v-progress-linear__indeterminate.long),
.ln-panel :deep(.v-data-table-progress .v-progress-linear__indeterminate.short),
.ln-panel :deep(.v-data-table-progress .v-progress-linear__determinate) {
  background-color: var(--ln-ink);
  border-color: var(--ln-ink);
}

.ln-panel :deep(.v-data-table-progress .v-progress-linear__background) {
  background-color: var(--ln-line);
  border-color: var(--ln-line);
}

/* ---------- cells ------------------------------------------------------ */

.ln-cell-strong {
  font-weight: 500;
  color: var(--ln-ink);
}

.ln-cell-empty {
  color: var(--ln-muted);
  white-space: nowrap;
}

.ln-app {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.ln-cell-meta {
  font-size: 13px;
  color: var(--ln-muted);
  white-space: nowrap;
}

.ln-cell-num {
  font-family: var(--ln-mono);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--ln-ink);
}

.ln-url {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ln-url__text {
  font-family: var(--ln-mono);
  font-size: 12.5px;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
  /* letting this wrap is what forced it onto a 2nd line even with room to
     spare on desktop, since the 220px header width (needed as a floor so
     the column can't collapse at 600-960px) reads as a cap once wrapping is
     allowed; nowrap makes the table grow past that floor to fit the full
     URL instead, falling back to the wrapper's horizontal scroll only when
     it truly doesn't fit - same as the Note/Actions columns already do */
  white-space: nowrap;
}

.ln-url__copy {
  flex: 0 0 auto;
}

.ln-note {
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ln-muted);
}

.ln-tag {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 9999px;
  font-family: sans-serif;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: default;
}

.ln-tag--blue {
  background: var(--ln-blue-bg);
  color: var(--ln-blue-fg);
}

.ln-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.ln-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  color: var(--ln-muted);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-page a.ln-iconbtn {
  color: var(--ln-muted);
}

.ln-iconbtn:hover {
  background: var(--ln-surface-2);
  color: var(--ln-ink);
}

.ln-iconbtn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 1px;
}

.ln-iconbtn--danger:hover {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-iconbtn .v-icon {
  font-size: 17px;
  color: inherit;
}

/* ---------- empty state ------------------------------------------------ */

.ln-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px 64px;
  text-align: center;
}

.ln-empty__art {
  margin-bottom: 20px;
}

.ln-empty__title {
  margin: 0 0 8px;
  font-family: sans-serif;
  font-size: 22px;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
  /* the "No links match" title quotes the search, often a pasted store url */
  max-width: 100%;
  overflow-wrap: anywhere;
}

.ln-empty__body {
  margin: 0 0 24px;
  max-width: 38ch;
  font-size: 13px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- delete dialog ---------------------------------------------- */

.ln-dialog {
  padding: 28px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.ln-dialog__title {
  margin: 0 0 16px;
  font-family: sans-serif;
  font-size: 26px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-dialog__body p {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

.ln-dialog__note {
  color: var(--ln-muted) !important;
}

.ln-dialog__label {
  margin: 20px 0 6px !important;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted) !important;
}

.ln-dialog__target {
  font-family: var(--ln-mono);
  font-size: 13px;
  word-break: break-all;
  color: var(--ln-ink) !important;
}

.ln-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--ln-line);
}

/* ---------- breakpoints ------------------------------------------------ */

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .ln-display {
    font-size: 52px;
  }

  .ln-panel__head {
    padding: 10px 20px;
  }

  .ln-search {
    height: 46px;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }

  .ln-head {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  .ln-head__actions {
    padding-bottom: 4px;
  }
}
</style>
